import { take } from 'ramda';
import {
    paramChild,
    paramDsName,
    paramParent,
    paramXPathSrc,
} from './constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeNumber,
    typeXPath,
} from './constants/parameterTypes';
import { commandResult } from './helpers/commandResult';

const optionalParameters = [
    {
        name: paramDsName,
        description:
            'Name of the data source. Assumes you are using the Main data source if it is not specified',
        type: typeDataSource,
    },
    {
        name: paramChild,
        description: `Relative XPath to the child node (repeating) – must provide either /${paramChild} or /${paramXPathSrc}\n\nIf unspecified, the portion of /${paramXPathSrc} after the last slash will be used.`,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramParent,
    },
    {
        name: 'before',
        description:
            'Relative XPath from the parent node to the sibling node that child node should be inserted before. Ignored when using /posn. If node cannot be selected, simply inserts as usual.',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramParent,
    },
    {
        name: 'posn',
        description:
            '(whole number or the word "last". default: last) Row position where to insert. i.e. for /posn=3, rows will be inserted before the 3rd row. If /posn=last, rows will be inserted after the last row',
    },
    {
        name: 'count',
        description:
            '(number. default: 1) Number of occurrences to insert in each target location.',
        type: typeNumber,
        minValue: 1,
    },
    {
        name: paramXPathSrc,
        description:
            "Full XPath to the row to insert. The command uses this to determine the XML to use when it can't be determined based on /parent and /child.\n\nUsually necessary when /parent includes a filter/predicate, e.g. /my:ExpeneReport/my:Expenses[2]/my:Receipts",
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: 'firstparentonly',
        description:
            '(boolean, default: true) If /parent matches multiple nodes, only insert into the first node located. When false, insert into all located /parent nodes.',
        type: typeBoolean,
    },
];
const requiredParameters = [
    {
        name: paramParent,
        description:
            'XPath to the node(s) where new child rows should be inserted.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
];
const insertErrorMustSpecifyNode =
    'You must include either /child or /xpathsrc.';
const insertErrorChildSlash =
    'The child parameter value should not start with a slash.';
const insertErrorFailedToSelectSource =
    'Failed to determine the node to insert. You may need to use /xpathsrc';
const insertErrorFailedToSelectParent = 'Failed to select the parent node.';
const insertErrorInvalidPosition =
    'Position must be "last" or a positive integer.';
const insertErrorInvalidCount = 'Count must be a positive integer.';
const insertErrorFailedToInsert = 'Failed to insert the child node.';
const description =
    'While ExecuteAction is limited to the active view, Insert is not. It allows insertion of rows in a repeating table.';

const insertedCountToCommandResult = (insertedCount) =>
    commandResult(insertedCount !== 0, insertedCount, '');

async function performInsertAsync(
    child,
    posn,
    parentNode,
    childNode,
    beforeNode,
    position,
    insertCount,
) {
    try {
        let insertedCount = 0;
        let failedItemCount = 0;

        for (let i = 0; i < insertCount; i += 1) {
            const cloneNode = childNode.cloneNode(true);

            insertedCount += 1;

            if (posn === 'last') {
                // BUGBUG - It appears that /before logic is here inside the 'last' if-block because 'last' is the default value for /posn,
                //          but that means that /before supercedes /posn when /posn is 'last', contrary to the parameter descriptions
                //          Ideally, we should be distingushing between /posn specified and unspecified and only perform the /before
                //          insert when it is unspecified.
                if (beforeNode) {
                    await beforeNode.insertBeforeAsync(cloneNode);
                } else {
                    await parentNode.appendChildAsync(cloneNode);
                }
            } else {
                try {
                    await parentNode
                        .selectSingle(`${child}[${position}]`)
                        .insertBeforeAsync(cloneNode);
                } catch (e) {
                    // There is no node found at the specified position, so just append.
                    // BUGBUG: There is probably a better way to accomplish this, e.g. select all the matching child nodes and
                    //         check the length, etc.
                    posn = 'last';
                    insertedCount -= 1;
                    failedItemCount += 1;
                }
            }
        }

        // if any failed, try insert again as many times as it failed
        // BUGBUG there's probably a better way to go about this
        const secondTryInsertedCount =
            failedItemCount === 0
                ? 0
                : await performInsertAsync(
                      child,
                      posn,
                      parentNode,
                      childNode,
                      beforeNode,
                      position,
                      failedItemCount,
                  );

        return insertedCount + secondTryInsertedCount;
    } catch (e) {
        console.error('Error inserting child node:', e);

        throw new Error(insertErrorFailedToInsert);
    }
}

function performInsertOuterAsync(
    child,
    before,
    posn,
    count,
    parentNode,
    childNode,
) {
    const beforeNode = before ? parentNode.selectSingle(before) : null;

    // Ensure posn is a valid positive integer or the string "last".
    var position = 1;
    if (!posn || posn.toLowerCase() === 'last') {
        posn = 'last';
    } else {
        try {
            position = parseInt(posn);

            if (Number.isNaN(posn)) {
                throw new Error('Position is not a valid number');
            }

            if (position <= 1) {
                // Handle case of position not valid on the low end.
                position = 1;
            } else if (position > parentNode.selectNodes(child).length) {
                // Handle case of position not valid on the high end.
                posn = 'last';
            }
        } catch (e) {
            throw new Error(insertErrorInvalidPosition);
        }
    }

    // Ensure count is a valid positive integer.
    if (!count) {
        count = '1';
    }

    var insertCount = parseInt(count);
    if (Number.isNaN(insertCount) || insertCount <= 0) {
        throw new Error(insertErrorInvalidCount);
    }

    return performInsertAsync(
        child,
        posn,
        parentNode,
        childNode,
        beforeNode,
        position,
        insertCount,
    );
}

function Insert(params) {
    async function executeAsync() {
        var commonFunction = params.commonFunctions,
            dataSources = params.dataSources,
            dsname = commonFunction.getParamValue(paramDsName),
            parentXPath = commonFunction.getParamValue(paramParent),
            child = commonFunction.getParamValue(paramChild),
            before = commonFunction.getParamValue('before'),
            xpathsrc = commonFunction.getParamValue(paramXPathSrc),
            posn = commonFunction.getParamValue('posn'),
            count = commonFunction.getParamValue('count'),
            firstOnly = commonFunction.getBoolParamValue(
                'firstparentonly',
                true,
            );

        if (!child && !xpathsrc) {
            throw new Error(insertErrorMustSpecifyNode);
        }

        if (child && child.startsWith('/')) {
            throw new Error(insertErrorChildSlash);
        }

        if (!xpathsrc) {
            xpathsrc =
                parentXPath + (parentXPath.endsWith('/') ? '' : '/') + child;
        }

        if (!child) {
            // Need this for the selection for the actual insert to work below.
            child = xpathsrc.substr(xpathsrc.lastIndexOf('/') + 1);
        }

        var dom = dataSources.getDom(dsname);

        const sampleData = await params.sampleDataSource.initAsync();

        var childNode = sampleData.selectSampleNode(dsname, xpathsrc);

        if (!childNode) {
            throw new Error(insertErrorFailedToSelectSource);
        }

        // TODO: Try to add nils where appropriate - only applies to main data source for now

        const matchingParents = commonFunction.getValidNodeSet(
            dom,
            parentXPath,
            insertErrorFailedToSelectParent,
        );

        let addedCount = 0;

        const parentsToUse = firstOnly
            ? take(1, matchingParents)
            : matchingParents;

        for (const parentNode of parentsToUse) {
            const insertedCount = await performInsertOuterAsync(
                child,
                before,
                posn,
                count,
                parentNode,
                childNode,
            );

            addedCount += insertedCount;
        }

        return insertedCountToCommandResult(addedCount);
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default Insert;
